import { Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { FilterTh, describeColumnFilters } from '../common/column-filter-indicator'
/**
 * A detail page's History MODE, rendered as a real `ListPage`
 * (PROV-AUDIT).
 *
 * When `?view=history` is set, the detail page hands its whole surface
 * to this component instead of nesting a timeline inside its own
 * layout — so history behaves exactly like every other paged Admin
 * view: the RELEVANT filters (operation, kind of change, JST dates —
 * never the pinned entity/client, never the operator axis) live in the
 * sticky right rail with the collapse-and-count contract, the
 * full-width preference applies, and Previous/Next ride the fixed
 * bottom tier. "History" becomes the heading while the entity joins
 * the breadcrumb trail as a linked parent, and the same header spot
 * that entered the mode leads back out, so the operator never leaves
 * the entity's context.
 *
 * Mounted only while the mode is active: a normal detail visit costs
 * zero history requests.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys.js'
import {
  ClearFiltersButton,
  FilterField,
  FilteredEmptyState,
  ListPage,
  ListPagination,
} from '../common/list-page.js'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW, TABLE_WRAP } from '../common/table.js'
import { useResetOnChange } from '../common/use-reset-on-change.js'
import { DateFilterInput } from '../common/date-filter-input'
import { activityApi, messageForActivityError } from './api.js'
import {
  ActivityActorCell,
  ActivityChangeSummary,
  ActivityEntityLinks,
  ActivityWhen,
  ENTITY_TYPE_LABELS,
  FAMILY_LABELS,
  OPERATION_LABELS,
  useNow,
  jstDayEnd,
  jstDayStart,
} from './activity-presentation.js'
import { BackToDetailsLink } from './history-link.js'
import type {
  ActivityEntityType,
  ActivityFamily,
  ActivityOperation,
} from './types.js'

const PAGE_SIZE = 25
const ALL = '__all__'

const FAMILY_VALUES = Object.keys(FAMILY_LABELS) as ActivityFamily[]
const OPERATION_VALUES = Object.keys(OPERATION_LABELS) as ActivityOperation[]

export function EntityHistoryPage({
  entityType,
  entityId,
  clientId,
  entityLabel,
  entityNoun,
  detailsTo,
}: {
  entityType: ActivityEntityType
  entityId: string
  /** Tenant anchor; required by the API for every tenant-owned entity. */
  clientId?: string
  /** Names the entity on the Activity page's pivot banner. */
  entityLabel?: string
  /** How the empty state names the page's thing: "this cohort". */
  entityNoun: string
  /** The details view this history belongs to — the way back. */
  detailsTo: string
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  // One clock per table: the relative "When" column keeps telling the
  // truth while the page sits open.
  const now = useNow()

  const rawOperation = searchParams.get('op')
  const operation = OPERATION_VALUES.includes(rawOperation as ActivityOperation)
    ? (rawOperation as ActivityOperation)
    : undefined
  const rawFamily = searchParams.get('kind')
  const family = FAMILY_VALUES.includes(rawFamily as ActivityFamily)
    ? (rawFamily as ActivityFamily)
    : undefined
  const sinceDay = searchParams.get('since') ?? ''
  const untilDay = searchParams.get('until') ?? ''
  const rawSystem = searchParams.get('system')
  const systemEvents: 'exclude' | 'only' | undefined =
    rawSystem === 'exclude' || rawSystem === 'only' ? rawSystem : undefined

  function updateParams(mutate: (params: URLSearchParams) => void): void {
    const next = new URLSearchParams(searchParams)
    mutate(next)
    setSearchParams(next, { replace: true })
  }

  const hasFilters =
    operation !== undefined ||
    family !== undefined ||
    sinceDay !== '' ||
    untilDay !== '' ||
    systemEvents !== undefined
  const activeFilterCount = [
    operation !== undefined,
    family !== undefined,
    sinceDay !== '' || untilDay !== '',
    systemEvents !== undefined,
  ].filter(Boolean).length

  function clearFilters(): void {
    updateParams((params) => {
      params.delete('op')
      params.delete('kind')
      params.delete('since')
      params.delete('until')
      params.delete('system')
    })
  }

  const filterKey = [
    operation ?? '',
    family ?? '',
    sinceDay,
    untilDay,
    systemEvents ?? '',
  ].join('|')
  useResetOnChange(filterKey, () => setPage(0))

  const params = {
    entityType,
    entityId,
    clientId,
    family,
    operation,
    systemEvents,
    since: sinceDay === '' ? undefined : jstDayStart(sinceDay),
    until: untilDay === '' ? undefined : jstDayEnd(untilDay),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  // Student history reaches this route knowing only the user id; the
  // tenant anchor arrives with the detail row. Hold the timeline (it
  // renders as loading) rather than firing a request the API must 422.
  const anchorReady =
    entityType === 'client' || entityType === 'admin' || clientId !== undefined
  const timeline = useQuery({
    queryKey: queryKeys.activityEntity(params),
    queryFn: () => activityApi.entity(params),
    enabled: anchorReady,
  })
  const entries = timeline.data?.entries ?? []

  return (
    // "History" is the current page and therefore the heading; the
    // entity itself is the trail's final LINKED crumb (see
    // admin-breadcrumbs.tsx), which is the second way back beside the
    // header link.
    <ListPage
      title={
        <span className="flex items-center gap-2">
          {/* The same icon as the History header links, so the route
              reads as one feature. */}
          <Icon name="history" size={14} className="shrink-0 text-text-faint" />
          History
        </span>
      }
      action={<BackToDetailsLink to={detailsTo} />}
      // No text search exists over audit payloads (deliberately — see
      // activity-page.tsx), so the toolbar tier carries the entity's
      // identity instead: whose history this is stays on screen while
      // a long timeline scrolls under the sticky bars.
      search={
        <p className="truncate text-body text-text-secondary">
          {ENTITY_TYPE_LABELS[entityType]} ·{' '}
          <span className="font-medium text-text-primary">
            {entityLabel !== undefined && entityLabel !== ''
              ? entityLabel
              : `${entityId.slice(0, 8)}…`}
          </span>
        </p>
      }
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <FilterField active={operation !== undefined}
            label="Operation"
            htmlFor="history-operation"
            help="Removed = deletions, deactivations, endings."
          >
            <Select
              value={operation ?? ALL}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === ALL) params.delete('op')
                  else params.set('op', value)
                })
              }
            >
              <SelectTrigger id="history-operation" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any operation</SelectItem>
                {OPERATION_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {OPERATION_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={family !== undefined} label="Kind of change" htmlFor="history-kind">
            <Select
              value={family ?? ALL}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === ALL) params.delete('kind')
                  else params.set('kind', value)
                })
              }
            >
              <SelectTrigger id="history-kind" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All kinds</SelectItem>
                {FAMILY_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {FAMILY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={sinceDay !== ''} label="From" htmlFor="history-since" help="JST days, inclusive.">
            <DateFilterInput
              id="history-since"
              label="From"
              size="sm"
              value={sinceDay}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === '') params.delete('since')
                  else params.set('since', value)
                })
              }
            />
          </FilterField>
          <FilterField active={untilDay !== ''} label="To" htmlFor="history-until">
            <DateFilterInput
              id="history-until"
              label="To"
              size="sm"
              value={untilDay}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === '') params.delete('until')
                  else params.set('until', value)
                })
              }
            />
          </FilterField>
          {/* Three states on purpose: hiding automation noise and
              isolating it ("what did the sweeps do here?") are both real
              investigations, and "not set" must stay distinct from
              either. */}
          <FilterField active={systemEvents !== undefined} label="System events" htmlFor="history-system">
            <Select
              value={systemEvents ?? ALL}
              onValueChange={(value) =>
                updateParams((params) => {
                  if (value === ALL) params.delete('system')
                  else params.set('system', value)
                })
              }
            >
              <SelectTrigger id="history-system" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Shown</SelectItem>
                <SelectItem value="exclude">Hidden</SelectItem>
                <SelectItem value="only">Only system events</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </>
      }
      activeFilterCount={activeFilterCount}
      pagination={
        timeline.isSuccess && entries.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing events {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + entries.length}
              </>
            }
            previousDisabled={page === 0}
            nextDisabled={timeline.data?.hasMore !== true}
            onPrevious={() => setPage((value) => value - 1)}
            onNext={() => setPage((value) => value + 1)}
          />
        ) : undefined
      }
    >
      {timeline.isPending ? (
        <p className="text-text-secondary text-body">Loading history…</p>
      ) : timeline.isError ? (
        <div role="alert" className="text-body text-danger-text">
          {messageForActivityError(timeline.error)}
        </div>
      ) : entries.length === 0 ? (
        <FilteredEmptyState
          hasFilters={hasFilters}
          noun="events"
          onClear={clearFilters}
          emptyHint={`No provisioning activity recorded for ${entityNoun} yet.`}
        />
      ) : (
        <div className={TABLE_WRAP}>
          <table className={TABLE}>
            <thead>
              <tr className={TABLE_HEAD_ROW}>
                <FilterTh className="p-3 font-medium" label="When" filterDescription={describeColumnFilters(sinceDay && `On or after ${sinceDay}`, untilDay && `On or before ${untilDay}`)} />
                <FilterTh className="p-3 font-medium" label="What" filterDescription={describeColumnFilters(family && `Kind: ${family}`, operation && `Operation: ${operation}`)} />
                <FilterTh className="p-3 font-medium" label="Who" filterDescription={systemEvents === 'only' ? 'System events only' : systemEvents === 'exclude' ? 'System events excluded' : undefined} />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className={TABLE_ROW}>
                  <td className="p-3 align-top whitespace-nowrap">
                    <ActivityWhen occurredAt={entry.occurredAt} now={now} />
                  </td>
                  <td className="p-3 align-top">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span>{entry.label}</span>
                      <ActivityEntityLinks
                        entities={entry.entities}
                        skipEntityId={entityId}
                      />
                    </div>
                    <ActivityChangeSummary entry={entry} />
                  </td>
                  <td className="p-3 align-top">
                    <ActivityActorCell entry={entry} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  )
}
